// Corrective geometry for Ionic's picker wheels (`ion-picker-column`).
//
// THE DEFECT (Ionic 8.8.1)
// A picker column is a 200px-tall scroller of 34px rows, padded at each end by
// three 34px `.picker-item-empty` spacers — 102px. But centering a 34px row in
// a 200px viewport only needs (200 - 34) / 2 = 83px. The extra 19px at each end
// is scroll range *past* the position that centers the first / last option, and
// the scroller can rest there.
//
// It matters because of how ion-picker-column identifies the selection: on each
// scroll frame it calls `elementsFromPoint` at the scroller's exact center and
// looks for an ION-PICKER-COLUMN-OPTION. Anywhere in that 19px band the center
// sits over a spacer instead, the probe comes back undefined, and the handler
// skips BOTH the `.option-active` highlight and the 250ms settle timeout that
// commits the value via `setValue()`. Since every frame clears the previously
// pending timeout before re-probing, frames in the band actively cancel a
// commit that was already queued.
//
// User-visible result on the publish form: scroll past the last value and the
// row parks 19px above the highlight pill (pill empty, nothing active) and no
// `ionChange` ever fires — so the price/portions max-value explainer sheets
// never open and the value isn't persisted. Mandatory scroll-snap usually
// rescues it on release, which is why this reads as intermittent rather than
// constant.
//
// THE FIX
// Zero the spacers and replace them with exactly the padding centering needs.
// The first option then centers at scrollTop 0 and the last at scrollTop max —
// the dead band stops existing, so the center probe can never miss. Ionic's own
// `centerPickerItemInView` math is 2px off (it assumes the 102px lead-in);
// mandatory scroll-snap absorbs that, so no behavior is patched here, only
// layout.
//
// WHY IT'S INJECTED RATHER THAN WRITTEN IN global.scss
// `.picker-opts` and `.picker-item-empty` live in the column's shadow root and
// carry no `part` attribute, so page CSS cannot reach them. Setting a height on
// the `ion-picker-column` host doesn't work either — the scroller is capped
// independently by `max-height: 200px` inside the shadow root.
//
// Track https://github.com/ionic-team/ionic-framework — when this is fixed
// upstream the module self-retires (see `slackOf` below) and can be deleted.

import { logWarn } from './loggerService';

/** Marks a shadow root we've already injected into. */
const STYLE_MARK = 'data-hp-picker-geometry';

/** Columns whose geometry we've settled (patched, or found already correct). */
const settled = new WeakSet<Element>();
/** Columns waiting on first layout before they can be measured. */
const pending = new WeakMap<Element, ResizeObserver>();
/** Shadow roots we're already watching for late-built columns. */
const watchedRoots = new WeakSet<ShadowRoot>();

interface Geometry {
  /** Height of one option row, px. */
  rowHeight: number;
  /** Height of the scroll viewport, px. */
  viewportHeight: number;
  /** Scroll range past the position that centers the last option, px. */
  slack: number;
}

/**
 * Measures a column, or returns null if it isn't laid out yet (display:none,
 * not yet hydrated). Everything is read rather than hardcoded so an Ionic
 * upgrade that changes the row height or viewport can't silently misalign us.
 */
function measure(col: Element): Geometry | null {
  const scroller = col.shadowRoot?.querySelector<HTMLElement>('.picker-opts');
  if (!scroller) return null;

  const options = Array.from(col.querySelectorAll<HTMLElement>('ion-picker-column-option'));
  const rowHeight = options.find((o) => o.clientHeight > 0)?.clientHeight ?? 0;
  const viewportHeight = scroller.clientHeight;
  if (!rowHeight || !viewportHeight || viewportHeight < rowHeight) return null;

  // Snappable options only: Ionic sets `scroll-snap-align: none` on disabled
  // ones, so a trailing disabled option is not a resting place and must not be
  // what we measure the slack against.
  const snappable = options.filter((o) => !o.hasAttribute('disabled') && o.clientHeight > 0);
  const last = snappable[snappable.length - 1];
  if (!last) return null;

  const centersLast = last.offsetTop + last.clientHeight / 2 - viewportHeight / 2;
  return {
    rowHeight,
    viewportHeight,
    slack: scroller.scrollHeight - viewportHeight - centersLast,
  };
}

function correctionFor({ rowHeight, viewportHeight }: Geometry): string {
  const pad = (viewportHeight - rowHeight) / 2;
  // `box-sizing: border-box` is load-bearing. Ionic caps the scroller with
  // `max-height: 200px`, and shadow DOM does not inherit the app's global
  // border-box reset — left content-box, this padding stacks on top of the cap
  // and inflates the viewport past the height of the column's own host, which
  // drags the selected row out from under the highlight pill.
  //
  // padding-inline is deliberately untouched (Ionic's 16px).
  return `
.picker-item-empty { height: 0 !important; }
.picker-opts {
  box-sizing: border-box !important;
  padding-top: ${pad}px !important;
  padding-bottom: ${pad}px !important;
}
`;
}

/**
 * Applies the correction to one column.
 * @returns true when the column is settled and needs no further attention.
 */
function patch(col: Element): boolean {
  const shadow = col.shadowRoot;
  if (!shadow) return false;
  if (shadow.querySelector(`style[${STYLE_MARK}]`)) return true;

  const geometry = measure(col);
  if (!geometry) return false; // not laid out yet — a ResizeObserver will retry

  // Already correct: either a hidden column we mis-measured, or a future Ionic
  // that fixed this upstream. Either way, don't touch it.
  if (geometry.slack === 0) return true;

  const style = document.createElement('style');
  style.setAttribute(STYLE_MARK, '');
  style.textContent = correctionFor(geometry);
  shadow.appendChild(style);

  if (import.meta.env.DEV) {
    // Self-check: if the correction didn't land the wheel dead on, the
    // assumptions above no longer hold against this build of Ionic.
    requestAnimationFrame(() => {
      const after = measure(col);
      if (after && after.slack !== 0) {
        logWarn('Picker geometry correction did not zero the wheel slack', undefined, {
          before: geometry,
          after,
        });
      }
    });
  }
  return true;
}

/** Settles a column now, or arranges to retry when it's first laid out. */
function settle(col: Element): void {
  if (settled.has(col) || pending.has(col)) return;
  if (patch(col)) {
    settled.add(col);
    return;
  }
  // Not measurable yet. A column inside a collapsed section or a closed modal
  // has no layout, and nothing mutates when it later becomes visible — so a
  // MutationObserver would never revisit it. Watch its box instead.
  if (typeof ResizeObserver === 'undefined') return;
  const observer = new ResizeObserver(() => {
    if (!patch(col)) return;
    observer.disconnect();
    pending.delete(col);
    settled.add(col);
  });
  observer.observe(col);
  pending.set(col, observer);
}

/**
 * Collects picker columns anywhere in the tree, descending through open shadow
 * roots — the availability wheels are built inside IonDatetime's.
 */
function collect(root: Document | ShadowRoot, found: Element[] = []): Element[] {
  root.querySelectorAll('ion-picker-column').forEach((col) => found.push(col));
  root.querySelectorAll('*').forEach((el) => {
    const shadow = (el as HTMLElement).shadowRoot;
    if (!shadow) return;
    // Watch host shadow roots too: IonDatetime rebuilds its columns inside its
    // own shadow root (e.g. when `min` changes), which a light-DOM observer
    // never sees.
    if (!watchedRoots.has(shadow)) {
      watchedRoots.add(shadow);
      new MutationObserver(scheduleScan).observe(shadow, { childList: true, subtree: true });
    }
    collect(shadow, found);
  });
  return found;
}

let scanScheduled = false;
function scheduleScan(): void {
  if (scanScheduled) return;
  scanScheduled = true;
  // Coalesce to one pass per frame: hydrating a datetime fires a burst of
  // mutations, and the scan walks every shadow root in the document.
  requestAnimationFrame(() => {
    scanScheduled = false;
    collect(document).forEach(settle);
  });
}

let installed = false;

/**
 * Installs the correction for the lifetime of the document. Idempotent.
 * Safe to call before Ionic has hydrated — columns are picked up as they mount.
 */
export function initPickerGeometryFix(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  scheduleScan();
  new MutationObserver(scheduleScan).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}
